import { useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { FolderOpen, Ruler, Receipt, FileCheck, FileText, Upload, Trash2, X, Eye } from 'lucide-react';
import { useCompany } from '../context/CompanyContext';
import { useAuth } from '../context/AuthContext';
import { fileToDataUrl, resizeImage, formatDate, formatFileSize, cn } from '../utils/formatters';
import { input, label, modalOverlay, modalContent, modalHeader, modalBody, modalFooter } from '../styles/components';
import type { ProjectDocument, ProjectDocumentCategory } from '../types';

const CATEGORIES: { value: ProjectDocumentCategory; label: string; icon: typeof FileText }[] = [
  { value: 'project', label: 'Documentación del proyecto', icon: FolderOpen },
  { value: 'plan', label: 'Planos', icon: Ruler },
  { value: 'ticket', label: 'Ticket de compra', icon: Receipt },
  { value: 'invoice', label: 'Factura', icon: FileCheck },
  { value: 'contract', label: 'Contrato', icon: FileText },
  { value: 'other', label: 'Otros', icon: FileText },
];

const categoryInfo = (value: ProjectDocumentCategory) =>
  CATEGORIES.find(c => c.value === value) || CATEGORIES[CATEGORIES.length - 1];

const isImage = (doc: ProjectDocument) => (doc.mimeType || '').startsWith('image/');

export function ProjectDocuments({ projectId }: { projectId: string }) {
  const { documents, addProjectDocument, deleteProjectDocument } = useCompany();
  const { user } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [category, setCategory] = useState<ProjectDocumentCategory>('project');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<ProjectDocument | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const projectDocs = documents.filter(d => d.projectId === projectId);

  const reset = () => {
    setShowAdd(false);
    setCategory('project');
    setTitle('');
    setDescription('');
    setFile(null);
    setError('');
    if (fileRef.current) fileRef.current.value = '';
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    if (f && !title.trim()) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Selecciona un archivo (PDF o imagen).');
      return;
    }
    if (!title.trim()) {
      setError('Escribe un título para el documento.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let url = await fileToDataUrl(file);
      if (file.type.startsWith('image/')) {
        url = await resizeImage(url, 1600, 1600, 0.8);
      }
      await addProjectDocument({
        projectId,
        category,
        title: title.trim(),
        description: description.trim() || undefined,
        fileName: file.name,
        mimeType: file.type,
        url,
        size: file.size,
        uploadedBy: user?.id ?? '',
      });
      reset();
    } catch {
      setError('No se pudo guardar el documento. Puede que el archivo sea demasiado grande.');
    } finally {
      setSaving(false);
    }
  };

  const categoryLabel = (c: ProjectDocumentCategory) => CATEGORIES.find(x => x.value === c)?.label ?? 'Otros';

  return (
    <div className="b2b-panel">
      <div className="b2b-panel__head">
        <h2 className="text-lg font-semibold">Documentos del proyecto</h2>
        <button onClick={() => setShowAdd(true)} className="b2b-btn b2b-btn--primary b2b-btn--sm">
          <Upload className="w-4 h-4" /> Subir documento
        </button>
      </div>

      {projectDocs.length === 0 ? (
        <div className="p-8 text-center b2b-muted">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
          <p>Guarda aquí planos, tickets, facturas y documentación del proyecto.</p>
        </div>
      ) : (
        projectDocs.map(doc => {
          const cat = CATEGORIES.find(c => c.value === doc.category) ?? CATEGORIES[CATEGORIES.length - 1];
          const Icon = cat.icon;
          return (
            <div key={doc.id} className="b2b-panel__row">
              <span className="b2b-icon">
                {isImage(doc) ? (
                  <img src={doc.url} alt={doc.title} className="w-full h-full object-cover rounded-[inherit]" />
                ) : (
                  <Icon className="w-5 h-5" />
                )}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-medium truncate">{doc.title}</span>
                <span className="block text-xs b2b-muted truncate">
                  <span className="b2b-pill mr-1">{cat.label}</span>
                  {formatDate(doc.createdAt)}{doc.size ? ` · ${formatFileSize(doc.size)}` : ''}
                </span>
              </span>
              <button
                onClick={() => setPreview(doc)}
                className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]"
                aria-label="Ver documento"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={() => { if (confirm(`¿Eliminar "${doc.title}"?`)) deleteProjectDocument(doc.id); }}
                className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)] text-[color:var(--b2b-danger)]"
                aria-label="Eliminar documento"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          );
        })
      )}

      {showAdd && (
        <div className={modalOverlay} onClick={reset} role="dialog" aria-modal="true">
          <div className={cn(modalContent, 'max-w-md')} onClick={e => e.stopPropagation()}>
            <form onSubmit={handleSave}>
              <div className={modalHeader}>
                <h2 className="text-xl font-bold">Subir documento</h2>
                <button type="button" onClick={reset} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Cerrar">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className={modalBody}>
                {error && <p className="mb-3 text-sm text-[color:var(--b2b-danger)]">{error}</p>}
                <div className="space-y-3">
                  <div>
                    <label className={label}>Categoría</label>
                    <select className={input} value={category} onChange={e => setCategory(e.target.value as ProjectDocumentCategory)}>
                      {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={label}>Título *</label>
                    <input className={input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Ej: Plano de fontanería" />
                  </div>
                  <div>
                    <label className={label}>Archivo (PDF o imagen) *</label>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={onPick}
                      className={cn(input, 'file:mr-3 file:rounded-md file:border-0 file:px-3 file:py-1 file:text-sm file:font-medium file:bg-[var(--b2b-accent)] file:text-white')}
                    />
                  </div>
                  <div>
                    <label className={label}>Descripción (opcional)</label>
                    <input className={input} value={description} onChange={e => setDescription(e.target.value)} placeholder="Notas sobre este documento" />
                  </div>
                </div>
              </div>
              <div className={modalFooter}>
                <button type="button" onClick={reset} className="b2b-btn b2b-btn--ghost">Cancelar</button>
                <button type="submit" className="b2b-btn b2b-btn--primary" disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {preview && (
        <div className={modalOverlay} onClick={() => setPreview(null)} role="dialog" aria-modal="true">
          <div className={cn(modalContent, 'max-w-3xl')} onClick={e => e.stopPropagation()}>
            <div className={modalHeader}>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold truncate">{preview.title}</h2>
                <p className="text-xs b2b-muted">{categoryLabel(preview.category)}</p>
              </div>
              <button onClick={() => setPreview(null)} className="p-2 rounded-lg hover:bg-[var(--b2b-surface-muted)]" aria-label="Cerrar">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className={modalBody}>
              {isImage(preview) ? (
                <img src={preview.url} alt={preview.title} className="w-full max-h-[70vh] object-contain rounded-lg" />
              ) : (
                <iframe src={preview.url} title={preview.title} className="w-full h-[70vh] rounded-lg border border-[var(--b2b-border)]" />
              )}
              {preview.description && (
                <p className="mt-3 text-sm b2b-muted">{preview.description}</p>
              )}
            </div>
            <div className={modalFooter}>
              <a href={preview.url} download={preview.fileName || preview.title} className="b2b-btn b2b-btn--ghost">Descargar</a>
              <button onClick={() => setPreview(null)} className="b2b-btn b2b-btn--primary">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
